'use client';

import { useRef, useEffect } from 'react';
import Container from '../../layout/Container';
import Headings from '../../heading/Headings';
import FaqJsonLd from './FaqJsonLd';

export default function FaqSection({ block, position }) {
  const { title, background_color_picker, _metadata, expand_first_question } =
    block.faq;
  const limit = 750;
  const itemLinks = block.faq.faq;
  const detailsRefs = useRef([]);

  useEffect(() => {
    // Animate details content for cross-browser support
    const cleanups = [];

    detailsRefs.current.forEach((details, index) => {
      if (!details) return;

      const firstQuestionExpanded = expand_first_question && index === 0;
      const summary = details.querySelector('summary');
      const wrapper = details.querySelector('.faq-content-wrapper');
      if (!summary || !wrapper) return;

      if (firstQuestionExpanded) {
        details.open = true;
        summary.querySelector('.toggle-open')?.classList.add('rotated');
        // Explicit px height (CSS default is 0) so the close animation works
        wrapper.style.height = wrapper.firstElementChild.scrollHeight + 'px';
      }

      const handleClick = (e) => {
        e.preventDefault();

        const icon = summary.querySelector('.toggle-open');

        if (details.open) {
          if (icon) icon.classList.remove('rotated');

          const startHeight = wrapper.scrollHeight;
          wrapper.style.height = startHeight + 'px';
          wrapper.offsetHeight;

          // Animate to 0
          requestAnimationFrame(() => {
            wrapper.style.height = '0px';
          });

          // Wait for animation to finish, then close details
          setTimeout(() => {
            details.open = false;
          }, 310); // Add small buffer to prevent flicker
        } else {
          // First, close any other open details with animation
          detailsRefs.current.forEach((otherDetails, otherIndex) => {
            if (!otherDetails || otherIndex === index || !otherDetails.open)
              return;

            const otherWrapper = otherDetails.querySelector(
              '.faq-content-wrapper'
            );
            const otherIcon = otherDetails.querySelector('.toggle-open');
            if (!otherWrapper) return;

            // Rotate other arrow back immediately
            if (otherIcon) otherIcon.classList.remove('rotated');

            // Animate the other one closed
            const otherHeight = otherWrapper.scrollHeight;
            otherWrapper.style.height = otherHeight + 'px';
            otherWrapper.offsetHeight;

            requestAnimationFrame(() => {
              otherWrapper.style.height = '0px';
            });

            setTimeout(() => {
              otherDetails.open = false;
            }, 310);
          });

          details.open = true;

          // Rotate arrow immediately
          if (icon) icon.classList.add('rotated');

          wrapper.style.height = '0px';

          // Calculate height after opening
          requestAnimationFrame(() => {
            const content = wrapper.firstElementChild;
            const height = content.scrollHeight;
            wrapper.style.height = height + 'px';
          });
        }
      };

      summary.addEventListener('click', handleClick);

      cleanups.push(() => {
        summary.removeEventListener('click', handleClick);
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [itemLinks]);

  return (
    <section
      id={_metadata.uid + '_' + position}
      data-name={'faq'}
      className={`faq-section standard-padding bg-[${background_color_picker}]`}
    >
      <FaqJsonLd itemLinks={itemLinks} faqName={title} />
      <Container fullPage={false}>
        <div className="grid grid-cols-1 gap-4 sm:mb-0 sm:grid-cols-6">
          <div className="sm:col-span-2">
            <Headings
              headingContent={title}
              headingClassName="h4 sm:h2 text-center sm:text-left mx-auto sm:mx-0"
            />
          </div>
          <div className="sm:col-span-4">
            {itemLinks.map((item, index) => {
              const content_length = item?.section_content?.content?.length;

              return (
                <details
                  key={index}
                  ref={(el) => (detailsRefs.current[index] = el)}
                  className={`group border-dawn-grey-60 w-full border-b py-4 pr-2 pl-4 text-left focus:outline-none`}
                >
                  <summary className="flex w-full cursor-pointer justify-between [details[open]_&]:mb-5">
                    <h3 className="group-hover:text-cozy-cognac body-r sm:body-l">
                      {item?.section_content?.alt_title
                        ? item?.section_content?.alt_title
                        : item?.title}
                    </h3>
                    <span className="group-hover:bg-cozy-cognac toggle-open" />
                  </summary>
                  <div className="faq-content-wrapper">
                    <div
                      className={`${content_length > limit ? 'overflow-auto' : 'overflow-hidden'} max-h-screen`}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: item?.section_content?.content
                        }}
                        className="body-s rte block pb-4"
                      />
                    </div>
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
}
